/**
 * Single source of truth for ticket selection during checkout.
 * Cart UI reads via ticket-selection#renderCartFromStorage; ticket-selector writes here.
 * Legacy selectedTicket{id} keys are kept in sync for older callers and system tests.
 */

export const LEGACY_TICKET_PREFIX = 'selectedTicket';
export const LEGACY_VISIBLE_PREFIX = 'ticketSelectionVisibleTickets_';
// Which specific seat_ids are already in the cart for a given seating plan,
// used by the seating-plan controller to restore highlighting/state across a
// page refresh. Swept by clearAllTicketOrderStorage like the other prefixes.
export const SEATING_PLAN_SEATS_PREFIX = 'seatingPlanSeats_';
const CART_PREFIX = 'ticketCart_';

function emptyCart() {
  return { tickets: {}, eventTicketIds: [] };
}

// readCart() is called many times per user interaction (each cart getter
// calls it independently) and normally does a full sessionStorage scan +
// legacy-key merge every time. Cache the result for the currenty task cascade only,
// auto-expires via queueMicrotask so it expires on-tick.
let cartCache = null; // { eventSlug, cart }

function getCachedCart(eventSlug) {
  return cartCache?.eventSlug === eventSlug ? cartCache.cart : null;
}

function setCachedCart(eventSlug, cart) {
  cartCache = { eventSlug, cart };
  queueMicrotask(() => {
    if (cartCache?.eventSlug === eventSlug && cartCache.cart === cart) {
      cartCache = null;
    }
  });
}

export function cartStorageKey(eventSlug) {
  return `${CART_PREFIX}${eventSlug}`;
}

function normalizeCart(cart) {
  return {
    tickets:
      cart?.tickets && typeof cart.tickets === 'object' ? cart.tickets : {},
    eventTicketIds: Array.isArray(cart?.eventTicketIds)
      ? cart.eventTicketIds
          .map((id) => Number(id))
          .filter((id) => !Number.isNaN(id))
      : [],
  };
}

function parseJson(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function ticketIdFromLegacyKey(key) {
  if (!key.startsWith(LEGACY_TICKET_PREFIX)) return null;
  return key.slice(LEGACY_TICKET_PREFIX.length);
}

function syncLegacyTicketKeys(cart) {
  // Only add/update legacy keys here. Removals happen in setTicket() so a partial
  // writeCart (e.g. during a category tab frame swap) cannot wipe other tickets.
  Object.entries(cart.tickets).forEach(([id, state]) => {
    if (Number(state?.ticketSelectorQuantity) > 0) {
      sessionStorage.setItem(
        `${LEGACY_TICKET_PREFIX}${id}`,
        JSON.stringify(state),
      );
    }
  });
}

function syncLegacyVisibleTicketIds(eventSlug, eventTicketIds) {
  if (!eventSlug || !eventTicketIds?.length) return;

  sessionStorage.setItem(
    `${LEGACY_VISIBLE_PREFIX}${eventSlug}`,
    JSON.stringify(eventTicketIds.map((id) => ({ id: Number(id) }))),
  );
}

export function writeCart(eventSlug, cart) {
  if (!eventSlug) return;

  const normalized = normalizeCart(cart);
  sessionStorage.setItem(cartStorageKey(eventSlug), JSON.stringify(normalized));
  syncLegacyTicketKeys(normalized);
  syncLegacyVisibleTicketIds(eventSlug, normalized.eventTicketIds);

  setCachedCart(eventSlug, normalized);
}

function legacyTicketsFromSession() {
  const tickets = {};

  Object.keys(sessionStorage).forEach((key) => {
    const ticketId = ticketIdFromLegacyKey(key);
    if (!ticketId) return;

    const state = parseJson(sessionStorage.getItem(key));
    if (!state || Number(state.ticketSelectorQuantity) <= 0) return;

    tickets[ticketId] = state;
  });

  return tickets;
}

function legacyVisibleTicketIds(eventSlug) {
  const legacyVisible = parseJson(
    sessionStorage.getItem(`${LEGACY_VISIBLE_PREFIX}${eventSlug}`),
  );
  if (!legacyVisible?.length) return [];

  return legacyVisible
    .map((ticket) => Number(ticket?.id))
    .filter((id) => !Number.isNaN(id));
}

function mergeLegacyIntoCart(eventSlug, cart) {
  const legacyTickets = legacyTicketsFromSession();
  let changed = false;

  Object.entries(legacyTickets).forEach(([ticketId, state]) => {
    const existing = cart.tickets[ticketId];
    if (
      !existing ||
      Number(existing.ticketSelectorQuantity) <
        Number(state.ticketSelectorQuantity)
    ) {
      cart.tickets[ticketId] = state;
      changed = true;
    }
  });

  const legacyVisibleIds = legacyVisibleTicketIds(eventSlug);
  if (legacyVisibleIds.length) {
    const mergedIds = new Set([
      ...cart.eventTicketIds.map((id) => Number(id)),
      ...legacyVisibleIds,
      ...Object.keys(cart.tickets).map((id) => Number(id)),
    ]);
    const nextIds = Array.from(mergedIds);
    if (nextIds.length !== cart.eventTicketIds.length) {
      cart.eventTicketIds = nextIds;
      changed = true;
    }
  }

  Object.keys(cart.tickets).forEach((ticketId) => {
    const numericId = Number(ticketId);
    if (!Number.isNaN(numericId) && !cart.eventTicketIds.includes(numericId)) {
      cart.eventTicketIds.push(numericId);
      changed = true;
    }
  });

  if (changed) writeCart(eventSlug, cart);

  return cart;
}

function migrateFromLegacyKeys(eventSlug) {
  const cart = emptyCart();

  cart.tickets = legacyTicketsFromSession();
  cart.eventTicketIds = [
    ...Object.keys(cart.tickets).map((id) => Number(id)),
    ...legacyVisibleTicketIds(eventSlug),
  ];
  cart.eventTicketIds = [...new Set(cart.eventTicketIds)];

  if (Object.keys(cart.tickets).length || cart.eventTicketIds.length) {
    writeCart(eventSlug, cart);
  }

  return cart;
}

export function readCart(eventSlug) {
  if (!eventSlug) return emptyCart();

  const cached = getCachedCart(eventSlug);
  if (cached) return cached;

  const stored = parseJson(sessionStorage.getItem(cartStorageKey(eventSlug)));
  const cart = stored
    ? normalizeCart(stored)
    : migrateFromLegacyKeys(eventSlug);

  const merged = mergeLegacyIntoCart(eventSlug, cart);
  setCachedCart(eventSlug, merged);
  return merged;
}

export function getTicket(eventSlug, ticketId) {
  return readCart(eventSlug).tickets[String(ticketId)] || null;
}

export function setTicket(eventSlug, ticketId, state) {
  if (!eventSlug) return;

  const cart = readCart(eventSlug);
  const id = String(ticketId);

  if (!state || Number(state.ticketSelectorQuantity) <= 0) {
    delete cart.tickets[id];
    sessionStorage.removeItem(`${LEGACY_TICKET_PREFIX}${id}`);
  } else {
    cart.tickets[id] = state;
    if (!cart.eventTicketIds.includes(Number(id))) {
      cart.eventTicketIds.push(Number(id));
    }
  }

  writeCart(eventSlug, cart);
}

export function getTicketsWithQuantity(eventSlug) {
  return Object.values(readCart(eventSlug).tickets).filter(
    (ticket) => Number(ticket?.ticketSelectorQuantity) > 0,
  );
}

export function mergeEventTicketIds(eventSlug, ids) {
  if (!eventSlug) return [];

  const cart = readCart(eventSlug);
  const merged = new Set(cart.eventTicketIds.map((id) => Number(id)));

  (ids || []).forEach((id) => {
    const numericId = Number(id);
    if (!Number.isNaN(numericId)) merged.add(numericId);
  });

  Object.keys(cart.tickets).forEach((id) => merged.add(Number(id)));

  cart.eventTicketIds = Array.from(merged);
  writeCart(eventSlug, cart);
  return cart.eventTicketIds;
}

export function parseVisibleTicketsJson(raw) {
  const parsed = parseJson(raw);
  if (!parsed?.length) return [];

  return parsed
    .map((ticket) => Number(ticket?.id))
    .filter((id) => !Number.isNaN(id));
}

export function preferredVisibleTicketIdList(...lists) {
  return lists.reduce((best, current) => {
    if (!current?.length) return best;
    if (!best?.length || current.length > best.length) return current;
    return best;
  }, []);
}

export function totalQuantity(eventSlug) {
  return getTicketsWithQuantity(eventSlug).reduce(
    (total, ticket) => total + Number(ticket.ticketSelectorQuantity || 0),
    0,
  );
}

export function sumTicketAttribute(eventSlug, attr) {
  const suffix =
    attr === 'Quantity'
      ? 'ticketSelectorQuantity'
      : `ticketSelectorTotal${attr}`;

  return getTicketsWithQuantity(eventSlug).reduce((total, ticket) => {
    return total + Number(ticket[suffix] || 0);
  }, 0);
}

export function clearAllTicketOrderStorage() {
  Object.keys(sessionStorage).forEach((key) => {
    if (
      key.startsWith(LEGACY_TICKET_PREFIX) ||
      key.startsWith(CART_PREFIX) ||
      key.startsWith(LEGACY_VISIBLE_PREFIX) ||
      key.startsWith(SEATING_PLAN_SEATS_PREFIX)
    ) {
      sessionStorage.removeItem(key);
    }
  });

  cartCache = null;
}
